import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/ui/card";
import { Badge } from "./ui/ui/badge";
import { Button } from "./ui/ui/button";
import { Calendar, MapPin, Users, ExternalLink, Clock, Building } from "lucide-react";

const EventsPage: React.FC = () => {
  const upcomingEvents = [
    {
      id: "upcoming-1",
      title: "Hack AI - Tech Party!",
      date: "July 10th, 2025",
      time: "6:00pm - 8:00pm",
      location: "Capital Factory, 8th floor",
      theme: "AI & Networking",
      project: "Build, Demo, and Connect",
      sponsor: {
        name: "TBA",
        website: "",
        logo: null
      },
      description: "Experience Austin's premier AI community event! Connect with fellow builders, discover the latest AI innovations, and form meaningful partnerships that accelerate your projects and career.",
      isUpcoming: true
    }
  ];

  const pastEvents = [
    {
      id: "past-1",
      title: "Fine-tuning Meta's Llama 3.1 Model",
      date: "August 1st, 2024",
      theme: "LLM Fine-tuning",
      project: "Fine-tuning Meta's Llama 3.1 model",
      sponsor: {
        name: "Brev",
        website: "https://brev.dev",
        logo: "./sponsors/brevdev_logo.jpeg"
      },
      description: "Our inaugural event focused on fine-tuning Meta's Llama 3.1 model. Participants learned about the intricacies of large language models and how to customize them for specific use cases.",
      featuredSpeaker: {
        name: "Carter Abdallah",
        image: "./speakers/carter_brev.jpeg"
      },
      isUpcoming: false
    },
    {
      id: "past-2",
      title: "Creating AI Voice Personas with Sindarin",
      date: "September 5th, 2024",
      theme: "Voice AI",
      project: "Creating an AI Voice Persona",
      sponsor: {
        name: "Sindarin",
        website: "https://www.sindarin.tech/",
        logo: "https://pbs.twimg.com/profile_images/1742285356135780352/wwa4Oiu8_400x400.jpg"
      },
      description: "Our 2nd event focused on building AI personas with the Sindarin platform. Participants built no-code AI personas using the Sindarin platform or extended a code-based AI cashier.",
      featuredSpeaker: {
        name: "Brian Schneider",
        image: "./speakers/brian_sindarin.jpeg"
      },
      isUpcoming: false
    }
  ];

  const EventCard = ({ event }: { event: any }) => (
    <Card className="bg-card/30 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all duration-300 group">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Badge variant={event.isUpcoming ? "default" : "secondary"} className="font-mono text-xs">
                {event.isUpcoming ? "UPCOMING" : "PAST EVENT"}
              </Badge>
              {event.theme && (
                <Badge variant="outline" className="text-xs">
                  {event.theme}
                </Badge>
              )}
            </div>
            <CardTitle className="text-xl font-bold text-foreground group-hover:text-primary transition-colors">
              {event.title}
            </CardTitle>
            <CardDescription>{event.description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Event Details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 text-primary" />
            <span className="text-muted-foreground">{event.date}</span>
          </div>
          {event.time && (
            <div className="flex items-center space-x-2">
              <Clock className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">{event.time}</span>
            </div>
          )}
          {event.location && (
            <div className="flex items-center space-x-2">
              <MapPin className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">{event.location}</span>
            </div>
          )}
          {event.project && (
            <div className="flex items-center space-x-2">
              <Users className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">{event.project}</span>
            </div>
          )}
        </div>

        {/* Sponsor */}
        {event.sponsor && (
          <div className="flex items-center justify-between p-3 rounded-lg bg-card/20 border border-border/30">
            <div className="flex items-center space-x-3">
              <Building className="h-4 w-4 text-primary" />
              <div>
                <span className="text-sm font-medium text-foreground">Sponsored by</span>
                <p className="text-sm text-muted-foreground">{event.sponsor.name}</p>
              </div>
            </div>
            {event.sponsor.website && (
              <Button variant="outline" size="sm" asChild>
                <a href={event.sponsor.website} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-3 w-3 mr-1" />
                  Visit
                </a>
              </Button>
            )}
          </div>
        )}

        {/* Featured Speaker */}
        {event.featuredSpeaker && (
          <div className="flex items-center space-x-3 p-3 rounded-lg bg-card/20 border border-border/30">
            {event.featuredSpeaker.image && (
              <img
                src={event.featuredSpeaker.image}
                alt={event.featuredSpeaker.name}
                className="w-10 h-10 rounded-full object-cover"
              />
            )}
            <div>
              <span className="text-sm font-medium text-foreground">Featured Speaker</span>
              <p className="text-sm text-muted-foreground">{event.featuredSpeaker.name}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen py-20 px-4">
      <div className="container max-w-6xl mx-auto space-y-12">
        {/* Header */}
        <div className="text-center space-y-4">
          <Badge variant="outline" className="border-primary text-primary">
            <Calendar className="h-3 w-3 mr-1" />
            Events
          </Badge>
          <h1 className="text-4xl lg:text-5xl font-bold font-mono text-primary">
            ./events --all
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Join Austin's most connected AI community. Discover upcoming meetups and past events 
            featuring expert speakers, cutting-edge demos, and meaningful networking.
          </p>
        </div>

        {/* Upcoming Events */}
        {upcomingEvents.length > 0 && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold font-mono text-primary flex items-center space-x-2">
              <span>$ ls upcoming/</span>
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {upcomingEvents.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          </section>
        )}

        {/* Past Events */}
        <section className="space-y-6">
          <h2 className="text-2xl font-bold font-mono text-primary flex items-center space-x-2">
            <span>$ ls archive/</span>
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {pastEvents.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        </section>

        {/* Call to Action */}
        <Card className="bg-card/30 backdrop-blur-sm border-border/50 text-center">
          <CardContent className="py-8">
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Want to stay updated?
            </h3>
            <p className="text-muted-foreground mb-4">
              Join our community to get notified about upcoming events
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild>
                <a href="https://lu.ma/hackai-techparty" target="_blank" rel="noopener noreferrer">
                  Sign up on Luma
                </a>
              </Button>
              <Button variant="outline" asChild>
                <a href="https://www.meetup.com/hack-ai/" target="_blank" rel="noopener noreferrer">
                  Join Meetup
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EventsPage;